import type { StoredPart, StoredUploadSession } from './types';

const PREFIX = 'r2uploader:session:';
const MAX_AGE_MS = 48 * 60 * 60 * 1000;
const GROUP_ID_KEY = 'r2uploader:pairGroupId';
const ACTIVE_RENDER_KEY = 'r2uploader:activeRenderGroupId';

export function makeFingerprint(name: string, size: number, lastModified: number): string {
  return `${name}|${size}|${lastModified}`;
}

function keyFor(fingerprint: string): string {
  return PREFIX + fingerprint;
}

function safe<T>(fn: () => T, fallback: T): T {
  try {
    return fn();
  } catch {
    return fallback;
  }
}

export function saveSession(session: StoredUploadSession): void {
  safe(() => localStorage.setItem(keyFor(session.fingerprint), JSON.stringify(session)), undefined);
}

export function loadSession(fingerprint: string): StoredUploadSession | undefined {
  return safe(() => {
    const raw = localStorage.getItem(keyFor(fingerprint));
    if (!raw) return undefined;
    return JSON.parse(raw) as StoredUploadSession;
  }, undefined);
}

export function removeSession(fingerprint: string): void {
  safe(() => localStorage.removeItem(keyFor(fingerprint)), undefined);
}

export function updateCompletedParts(fingerprint: string, parts: StoredPart[]): void {
  const existing = loadSession(fingerprint);
  if (!existing) return;
  existing.completedParts = parts;
  saveSession(existing);
}

/** All unfinished sessions still recorded locally, pruning anything stale. */
export function listUnfinishedSessions(): StoredUploadSession[] {
  const results: StoredUploadSession[] = [];
  safe(() => {
    const now = Date.now();
    const staleKeys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(PREFIX)) continue;
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      try {
        const session = JSON.parse(raw) as StoredUploadSession;
        if (now - session.savedAt > MAX_AGE_MS) {
          staleKeys.push(key);
        } else {
          results.push(session);
        }
      } catch {
        staleKeys.push(key);
      }
    }
    for (const key of staleKeys) localStorage.removeItem(key);
    return undefined;
  }, undefined);
  return results;
}

/**
 * Both halves of a video+subtitle pair need to keep sharing one groupId
 * across a page reload so the server can still match them up, even if one
 * side's session expired and had to be re-created. Persisted separately
 * from the per-file sessions above, and cleared once the pair is done.
 */
export function getOrCreatePairGroupId(): string {
  return safe(() => {
    const existing = localStorage.getItem(GROUP_ID_KEY);
    if (existing) return existing;
    const id = crypto.randomUUID();
    localStorage.setItem(GROUP_ID_KEY, id);
    return id;
  }, crypto.randomUUID());
}

export function resetPairGroupId(): string {
  safe(() => localStorage.removeItem(GROUP_ID_KEY), undefined);
  return getOrCreatePairGroupId();
}

/**
 * Which (if any) completed pair currently has a render job worth polling
 * for. Kept separate from the pairGroupId above, which rotates the instant
 * a pair completes — this one is what survives a reload so progress for an
 * hours-long render keeps showing up after the user reopens the tab.
 */
export function getActiveRenderGroupId(): string | undefined {
  return safe(() => localStorage.getItem(ACTIVE_RENDER_KEY) ?? undefined, undefined);
}

export function setActiveRenderGroupId(groupId: string): void {
  safe(() => localStorage.setItem(ACTIVE_RENDER_KEY, groupId), undefined);
}

export function clearActiveRenderGroupId(): void {
  safe(() => localStorage.removeItem(ACTIVE_RENDER_KEY), undefined);
}
